import AppLogoIcon from "~/components/app-logo-icon";
import { InternalShareDropTray } from "~/components/channels/internal-share-drop-tray";
import DisplayNotifications from "~/components/info/display-resume-notifications";
import { ShiftReminderBanner } from "~/components/shift-reminder-banner";
import { CommandPalette } from "~/components/command-palette";
import { UserMenuContent } from "~/components/user-menu-content";
import { CompanyUserAvatar } from "~/components/user/company-user-avatar";
import { useTopNavigationStore } from "~/components/zustand/top-navigation-store";
import {
  NavLauncher,
  navItemHref,
  navPreferencesPayload,
  isSettingsNavItem,
  type ResolvedNav,
} from "~/components/nav-launcher";
import { NavBarEditor } from "~/components/nav-bar-editor";
import { useNavLauncherStore } from "~/components/zustand/nav-launcher-store";
import { navIcon } from "~/constants/nav-catalog";
import { Button } from "~/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { urlFor } from "~/client";
import { useNavigationPermissions } from "~/hooks/use-navigation-permissions";
import { usePlanFeatures } from "~/hooks/use-plan-features";
import { useTransmitChannel } from "~/hooks/use-transmit-channel";
import { usePermissions } from "~/hooks/usePermissions";
import {
  installAudioUnlockListeners,
  requestNotificationSound,
} from "~/lib/audio-notification-coordinator";
import { normalizeNotificationSoundSettings } from "~/lib/notification-sounds";
import { cn } from "~/lib/utils";
import { type BreadcrumbItem, type Company, type SharedData } from "~/types";
import { hasExpiredPresenceMember, workspaceMutationGuard } from "@/lib/chat-page-mutations";
import type { Data } from "@generated/data";
import type {
  CompanyUserPresenceUpdateEvent,
  WorkspaceChannel,
  WorkspaceDirectMessage,
  WorkspaceRealtimeEvent,
  WorkspaceTeam,
} from "~/types/workspace";
import { Link, router, usePage, usePoll } from "@inertiajs/react";
import axios, { isCancel } from "axios";
import {
  AtSign,
  Bot,
  ChartColumn,
  Check,
  ClipboardList,
  Handshake,
  LifeBuoy,
  Mail,
  MessageCircle,
  MessagesSquare,
  Search,
  Settings,
  Ticket,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type NavigationIndicatorKey =
  | "chatHasUnread"
  | "emailHasUnread"
  | "socialCommentsHasUnread"
  | "formsHasUnread"
  | "ticketsHasOpen"
  | "supportHasOpen"
  | "dealsCopilotHasPending";

type NavigationIndicators = Record<NavigationIndicatorKey, boolean | null>;

/** The two "doors" onto the ticket index (same route, different `type`). */
type TicketSectionKey = "task" | "support";

type WorkspaceDestination = {
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  badge?: string;
  indicatorKey?: NavigationIndicatorKey;
  indicator?: boolean;
  // When set, this destination is a ticket-index SECTION door (Tareas/Soporte).
  // Both share the same path, so href-based highlighting can't tell them apart;
  // active state is decided by the current page's `section` prop instead.
  sectionKey?: TicketSectionKey;
};

/** Per-conversation unread snapshot used to detect sound-worthy increases. */
type UnreadSnapshotEntry = {
  unreadCount: number;
  muted: boolean;
};

type WorkspaceSharedData = SharedData & {
  auth: SharedData["auth"] & {
    companies?: Company[];
    status?: string;
  };
  workspaceTeams?: WorkspaceTeam[];
  workspaceChannels?: WorkspaceChannel[];
  workspaceDirectMessages?: WorkspaceDirectMessage[];
  navigationIndicators?: NavigationIndicators;
  // Resolved configurable navigation (bar + launcher). Undefined for
  // guest/widget/no-company. Declared here to unwrap the generated AlwaysProp
  // wrapper to the real runtime shape.
  navigation?: ResolvedNav;
  workspaceBrand?: Data.WorkspaceBrand | null;
};

type WorkspaceShellProps = {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  hideHeaderOnMobile?: boolean;
  bottomBar?: ReactNode;
};

const isExternalHref = (href: string): boolean => /^https?:\/\//.test(href);

const isActiveHref = (pageUrl: string, href: string) => {
  if (pageUrl === href) {
    return true;
  }

  return href !== "/" && pageUrl.startsWith(`${href}/`);
};

function nextUnreadCount(current: number, delta: number, absolute: number | null): number {
  if (absolute !== null) {
    return absolute;
  }

  return Math.max(0, current + delta);
}

type StatusOption = {
  value: string;
  label: string;
  color: string;
};

function WorkspaceTopNavigation({
  destinations,
  pageUrl,
  activeTicketSection,
  showLabels,
  navigationIndicators,
  trailing,
}: {
  destinations: WorkspaceDestination[];
  pageUrl: string;
  // Present only while viewing the ticket index; disambiguates the two section
  // doors (Tareas/Soporte) that would otherwise share the same path.
  activeTicketSection: TicketSectionKey | undefined;
  showLabels: boolean;
  navigationIndicators: NavigationIndicators | undefined;
  // Rendered at the end of the bar (universal launcher). Optional so the
  // guest/widget fallback path can omit it.
  trailing?: ReactNode;
}) {
  const hasNavigationIndicator = (destination: WorkspaceDestination): boolean => {
    if (destination.indicator === true) {
      return true;
    }

    if (!destination.indicatorKey) {
      return false;
    }

    return navigationIndicators?.[destination.indicatorKey] === true;
  };

  return (
    <nav className="bg-muted flex min-w-0 shrink items-center gap-1 overflow-x-auto rounded-lg p-1">
      {destinations.map((item) => {
        const isExternal = isExternalHref(item.href);
        // can't be Inertia client-side visits — render a plain anchor
        // with identical styling. Active highlighting doesn't apply to
        // a different host, so we rely on the badge instead.
        // Section doors (Tareas/Soporte) share a path, so href matching would
        // light both up (or neither, once filters add query params). Decide
        // those by the current page's section prop instead.
        const isActive = item.sectionKey
          ? activeTicketSection === item.sectionKey
          : !isExternal && isActiveHref(pageUrl, item.href);
        const hasIndicator = hasNavigationIndicator(item);
        const className = cn(
          "text-muted-foreground hover:text-foreground flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition",
          isActive && "bg-background text-foreground shadow-sm",
        );
        const content = (
          <>
            <item.icon className={cn("size-4", !showLabels && hasIndicator && "text-primary")} />
            {showLabels && <span className="hidden sm:inline">{item.label}</span>}
            {showLabels && hasIndicator && (
              <span className="bg-primary size-2 rounded-full" aria-hidden="true" />
            )}
            {item.badge && (
              <span className="rounded-full bg-fuchsia-600 px-1.5 text-[10px] font-bold text-white">
                {item.badge}
              </span>
            )}
          </>
        );

        if (isExternal) {
          return (
            <a
              key={item.label}
              href={item.href}
              title={showLabels ? undefined : item.label}
              aria-label={item.label}
              className={className}
            >
              {content}
            </a>
          );
        }

        return (
          <Link
            key={item.label}
            href={item.href}
            prefetch
            title={showLabels ? undefined : item.label}
            aria-label={item.label}
            className={className}
          >
            {content}
          </Link>
        );
      })}
      {trailing}
    </nav>
  );
}

function WorkspaceUserMenu({
  auth,
  status,
  statusOptions,
  currentStatusOption,
  settingsDestination,
  onStatusChange,
}: {
  auth: WorkspaceSharedData["auth"];
  status: string;
  statusOptions: StatusOption[];
  currentStatusOption: StatusOption;
  settingsDestination: { label: string; href: string } | null;
  onStatusChange: (value: string) => void;
}) {
  const { t } = useTranslation("home_common");

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("user_menu", "User menu")}>
          <span className="relative">
            <CompanyUserAvatar
              userId={auth.user.id}
              name={auth.user.name}
              email={auth.user.email}
              avatar={auth.user.avatar}
              className="size-8 text-xs"
            />
            <span
              className={cn(
                "ring-background absolute -right-1 -bottom-1 size-3 rounded-full ring-2",
                currentStatusOption.color,
              )}
              title={currentStatusOption.label}
              aria-label={currentStatusOption.label}
            />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end">
        {statusOptions.map((option) => (
          <DropdownMenuItem key={option.value} onSelect={() => onStatusChange(option.value)}>
            <span className={cn("h-3 w-3 rounded-full", option.color)} />
            <span className="flex-1">{option.label}</span>
            {option.value === status && <Check className="size-4" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {settingsDestination && (
          <>
            <DropdownMenuItem asChild>
              <Link className="block w-full" href={settingsDestination.href} as="button" prefetch>
                <Settings className="mr-2" />
                {settingsDestination.label}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <UserMenuContent companies={auth.companies ?? []} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function WorkspaceShell({
  children,
  breadcrumbs: _breadcrumbs = [],
  hideHeaderOnMobile = false,
  bottomBar,
}: WorkspaceShellProps) {
  // Both namespaces: bar labels are `home_common`, but pinned `settings-*` items
  // resolve their `tabs.*` labels from `home_settings_setting`.
  const { t } = useTranslation(["home_common", "home_settings_setting"]);
  const showTopNavigationLabels = useTopNavigationStore((state) => state.display !== "icons");
  useEffect(() => {
    const previousHtmlOverflowY = document.documentElement.style.overflowY;
    const previousBodyOverflowY = document.body.style.overflowY;

    document.documentElement.style.overflowY = "hidden";
    document.body.style.overflowY = "hidden";

    return () => {
      document.documentElement.style.overflowY = previousHtmlOverflowY;
      document.body.style.overflowY = previousBodyOverflowY;
    };
  }, []);

  const page = usePage<WorkspaceSharedData>();
  const { auth, workspaceBrand } = page.props;
  const { can } = usePermissions();

  // The workspace is persistent across Inertia visits. Apply and clear every
  // overridden token from the document root so switching organizations cannot
  // leave the previous organization's colors behind in portalled UI.
  useEffect(() => {
    if (!workspaceBrand) {
      return;
    }

    const root = document.documentElement;
    const tokens = [
      ["--primary", workspaceBrand.primaryColor],
      ["--primary-foreground", workspaceBrand.primaryForeground],
      ["--ring", workspaceBrand.primaryColor],
      ["--sidebar-primary", workspaceBrand.primaryColor],
      ["--sidebar-primary-foreground", workspaceBrand.primaryForeground],
    ] as const;

    for (const [name, value] of tokens) {
      root.style.setProperty(name, value);
    }

    return () => {
      for (const [name] of tokens) {
        root.style.removeProperty(name);
      }
    };
  }, [workspaceBrand]);
  const { canViewNavItem } = useNavigationPermissions();
  const { hasFeature } = usePlanFeatures();
  const {
    workspaceTeams: teamsProp,
    workspaceChannels: workspaceChannelsProp,
    workspaceDirectMessages: directMessagesProp,
  } = page.props;
  const teams = useMemo(() => teamsProp ?? [], [teamsProp]);
  const workspaceChannels = useMemo(() => workspaceChannelsProp ?? [], [workspaceChannelsProp]);
  const directMessages = useMemo(() => directMessagesProp ?? [], [directMessagesProp]);
  // Cheap preflight snapshots so background producers (presence-expiry timer,
  // mute events) can skip a true no-op BEFORE ever calling the router, not
  // just rely on the patched core's post-hoc deep-equal check.
  const directMessagesRef = useRef(directMessages);
  useEffect(() => {
    directMessagesRef.current = directMessages;
  }, [directMessages]);
  // Only the ticket index sets `section`; elsewhere it's undefined so neither
  // ticket door lights up. When a ticket detail is open (show route), the
  // highlight follows the ticket's OWN type, so a deep-linked support case lights
  // up Soporte even though the board defaults to Tareas. Unclassified (null)
  // tickets fall back to the board's section.
  const openTicketType = (page.props as { ticketDetail?: { type?: TicketSectionKey | null } })
    .ticketDetail?.type;
  const boardSection = (page.props as { section?: TicketSectionKey }).section;
  const activeTicketSection = openTicketType ?? boardSection;
  const navigationIndicators = page.props.navigationIndicators;
  const canViewWorkspaceConversations =
    hasFeature("chat_channels" as App.Enums.PlanFeatureCodeEnum) &&
    can("view-channels" as App.Enums.PermissionEnum);
  const hasExistingDirectMessages = directMessages.some((member) => member.conversationId !== null);
  const canViewDirectMessages = canViewWorkspaceConversations || hasExistingDirectMessages;
  const canViewForms =
    can("view-forms" as App.Enums.PermissionEnum) ||
    can("manage-forms" as App.Enums.PermissionEnum);
  const canViewReports = can("manage-company" as App.Enums.PermissionEnum);
  const internalUnreadTotal =
    teams.reduce((total, team) => total + team.unreadCount, 0) +
    workspaceChannels.reduce((total, channel) => total + channel.unreadCount, 0) +
    directMessages.reduce((total, member) => total + member.unreadCount, 0);
  const hasInternalUnread = internalUnreadTotal > 0;
  // Muted conversations keep their unread badge but must not trigger the
  // notification sound. The sound watches per-conversation unread counts (with
  // their mute state) instead of an aggregate total, so unmuting a conversation
  // with unreads, mute-expiry reloads, or a mute masking another conversation's
  // increment in the same tick cannot create or swallow dings.
  const previousUnreadSnapshotRef = useRef<Map<string, UnreadSnapshotEntry> | null>(null);
  const notificationSoundSettings = auth.notificationSoundSettings;

  useEffect(() => installAudioUnlockListeners(), []);

  useEffect(() => {
    const current = new Map<string, UnreadSnapshotEntry>();
    for (const team of teams) {
      current.set(`team:${team.id}`, {
        unreadCount: team.unreadCount,
        muted: false,
      });
    }
    for (const channel of workspaceChannels) {
      current.set(`channel:${channel.id}`, {
        unreadCount: channel.unreadCount,
        muted: channel.muted,
      });
    }
    for (const member of directMessages) {
      if (member.conversationId === null) {
        continue;
      }
      current.set(`dm:${member.conversationId}`, {
        unreadCount: member.unreadCount,
        muted: member.muted,
      });
    }

    const previous = previousUnreadSnapshotRef.current;
    // The listings are capped, so conversations drop out and re-enter without
    // their counts changing. Merge instead of replacing the snapshot: evicted
    // keys keep their last known entry, so cap re-entry compares against it
    // instead of looking brand new (which would ding on old unreads).
    const merged = new Map(previous ?? []);
    for (const [key, entry] of current) {
      merged.set(key, entry);
    }
    previousUnreadSnapshotRef.current = merged;
    if (previous === null) {
      return;
    }

    // A conversation dings only when it is non-muted NOW and its unread count
    // strictly increased; brand-new non-muted conversations with unreads count
    // as an increase from zero. The previous snapshot's mute state is ignored:
    // unmuting alone never changes unreadCount, so a same-tick unmute plus a
    // genuine new message must still ding.
    let notifiableSoundKey: string | null = null;
    for (const [key, entry] of current) {
      if (entry.muted || entry.unreadCount <= 0) {
        continue;
      }
      const previousEntry = previous.get(key);
      if (!previousEntry || entry.unreadCount > previousEntry.unreadCount) {
        notifiableSoundKey = `internal:${key}:${entry.unreadCount}`;
        break;
      }
    }
    if (!notifiableSoundKey) {
      return;
    }

    const soundSettings = normalizeNotificationSoundSettings(notificationSoundSettings);
    if (!soundSettings.enabled || soundSettings.mutedCategories.includes("internal")) {
      return;
    }

    requestNotificationSound({
      id: notifiableSoundKey,
      sound: soundSettings.internalSound,
      volume: soundSettings.volume,
    });
  }, [teams, workspaceChannels, directMessages, notificationSoundSettings]);
  const canViewEmail =
    hasFeature("email_channels" as App.Enums.PlanFeatureCodeEnum) &&
    can("view-channels" as App.Enums.PermissionEnum);
  const canViewSocialComments =
    hasFeature("social_comments" as App.Enums.PlanFeatureCodeEnum) &&
    can("view-social-comments" as App.Enums.PermissionEnum);
  const canViewContacts = canViewNavItem("contacts");
  const canViewDeals = canViewNavItem("deals");
  const canViewTickets = canViewNavItem("tickets");
  const canViewAutomations = canViewNavItem("automations");
  const canViewWidgets = canViewNavItem("widgets");
  const topDestinations = useMemo(() => {
    const candidates: Array<WorkspaceDestination | null> = [
      canViewWorkspaceConversations
        ? {
            label: t("chat"),
            href: urlFor("chat.index"),
            icon: MessageCircle,
            indicatorKey: "chatHasUnread",
          }
        : null,
      canViewWorkspaceConversations || canViewDirectMessages
        ? {
            label: t("internal", "Internal"),
            href: urlFor("channels.index"),
            icon: MessagesSquare,
            indicator: hasInternalUnread,
          }
        : null,
      canViewEmail
        ? {
            label: t("mail"),
            href: urlFor("mails.index", { folder: "inbox" }),
            icon: Mail,
            indicatorKey: "emailHasUnread",
          }
        : null,
      canViewSocialComments
        ? {
            label: t("comments"),
            href: urlFor("social-comments.index"),
            icon: AtSign,
            indicatorKey: "socialCommentsHasUnread",
          }
        : null,
      canViewForms
        ? {
            label: t("sidebar.forms", "Formularios"),
            href: urlFor("forms.index"),
            icon: ClipboardList,
            indicatorKey: "formsHasUnread",
          }
        : null,
      canViewContacts
        ? { label: t("sidebar.crm"), href: urlFor("contacts.index"), icon: Users }
        : null,
      canViewDeals
        ? {
            label: t("sidebar.deals", "Ventas"),
            href: urlFor("deals.index"),
            icon: Handshake,
          }
        : null,
      canViewTickets
        ? {
            label: t("sidebar.tickets"),
            href: urlFor("tickets.index"),
            icon: Ticket,
            indicatorKey: "ticketsHasOpen",
            sectionKey: "task",
          }
        : null,
      canViewTickets
        ? {
            // Soporte reuses the ticket index with `?type=support` (no new route,
            // so the Tuyau runtime registry is untouched — see repo bug #631).
            label: t("sidebar.support", "Soporte"),
            href: `${urlFor("tickets.index")}?type=support`,
            icon: LifeBuoy,
            indicatorKey: "supportHasOpen",
            sectionKey: "support",
          }
        : null,
      canViewAutomations
        ? {
            label: t("sidebar.automations"),
            href: urlFor("airie-bots.index"),
            icon: Bot,
          }
        : null,
      canViewReports
        ? {
            label: t("sidebar.reports", "Informes"),
            href: urlFor("reports.index"),
            icon: ChartColumn,
          }
        : null,
    ];

    return candidates.filter(
      (destination): destination is WorkspaceDestination => destination !== null,
    );
  }, [
    canViewAutomations,
    canViewContacts,
    canViewDeals,
    canViewDirectMessages,
    canViewEmail,
    canViewForms,
    canViewReports,
    canViewSocialComments,
    canViewTickets,
    canViewWorkspaceConversations,
    hasInternalUnread,
    t,
  ]);
  // Configurable navigation bar. The server resolves `navigation.bar` (ordered,
  // permission-filtered, per-user deltas applied) + `navigation.launcher` (all
  // permitted destinations). When present we render the resolved bar; otherwise
  // (guest/widget/no-company) we fall back to the legacy inline construction so
  // nothing regresses for those cases.
  const navigation = page.props.navigation;
  // Render `navigation.bar` verbatim: the server owns the correct default set
  // (today's 10 destinations for a non-customizer) and honors per-user pins, so
  // `settings-*`/`integrations`/`comments` appear here only when the user pinned
  // them. No client-side filtering — the server is the source of truth.
  const barDestinations = useMemo<WorkspaceDestination[]>(() => {
    if (!navigation) {
      return topDestinations;
    }
    return navigation.bar.map((item) => ({
      // `settings-*` labels live in the `home_settings_setting` namespace; all
      // other destinations in `home_common`.
      label: t(item.labelKey, {
        ns: isSettingsNavItem(item) ? "home_settings_setting" : "home_common",
        defaultValue: item.labelKey,
      }),
      href: navItemHref(item),
      icon: navIcon(item.iconName),
      // "internal" has no server indicatorKey; its unread is client-derived.
      indicator: item.id === "internal" ? hasInternalUnread : undefined,
      indicatorKey: item.indicatorKey as NavigationIndicatorKey | undefined,
      sectionKey: item.sectionKey as TicketSectionKey | undefined,
    }));
  }, [navigation, topDestinations, hasInternalUnread, t]);
  // Modo edición personal de la barra (in situ): estado efímero de cliente. El
  // launcher lo activa ("Personalizar barra…"); cada cambio persiste optimista.
  const editMode = useNavLauncherStore((state) => state.editMode);
  const setEditMode = useNavLauncherStore((state) => state.setEditMode);
  const launcherIds = useMemo(
    () => (navigation ? navigation.launcher.map((item) => item.id) : []),
    [navigation],
  );
  const handlePersonalNavChange = useCallback(
    (ids: string[]) => {
      router.patch(urlFor("nav-preferences.update"), navPreferencesPayload(ids, launcherIds), {
        preserveScroll: true,
        preserveState: true,
      });
    },
    [launcherIds],
  );
  const handleResetPersonalNav = useCallback(() => {
    router.delete(urlFor("nav-preferences.reset"), { preserveScroll: true });
  }, []);
  const settingsDestination = useMemo(
    () =>
      canViewWidgets
        ? {
            label: t("sidebar.integrations", "Integraciones"),
            href: urlFor("integrations"),
          }
        : null,
    [canViewWidgets, t],
  );
  const status = auth.status || "available";
  const statusOptions = [
    { value: "available", label: t("status.available"), color: "bg-green-500" },
    { value: "busy", label: t("status.busy"), color: "bg-red-500" },
    { value: "away", label: t("status.away"), color: "bg-yellow-400" },
    { value: "break", label: t("status.break"), color: "bg-orange-400" },
    { value: "offline", label: t("status.offline"), color: "bg-gray-400" },
  ];
  const currentStatusOption =
    statusOptions.find((option) => option.value === status) ?? statusOptions[0];
  const companyId = auth.company?.id ? Number(auth.company.id) : null;
  const currentUserId = auth.user?.id ? Number(auth.user.id) : null;

  // Realtime events update workspace props in-place. Polling remains a low-cost
  // consistency repair for missed events, sleeping tabs, reconnect gaps, and
  // structural changes that are still legacy-only while mobile is migrated.
  usePoll(30_000, {
    only: [
      "workspaceTeams",
      "workspaceChannels",
      "workspaceDirectMessages",
      "navigationIndicators",
    ],
  });

  const workspaceSidebarCompanyTopic = companyId ? `/${companyId}/workspace/sidebar-updates` : null;
  const workspaceSidebarUserTopic =
    companyId && currentUserId
      ? `/${companyId}/user/${currentUserId}/workspace/sidebar-updates`
      : null;
  const presenceTopic = companyId ? `/${companyId}/presence` : null;
  const replaceDirectMessagePresence = useCallback(
    (event: CompanyUserPresenceUpdateEvent) => {
      if (!companyId || !currentUserId) {
        return;
      }
      router.replaceProp(
        "workspaceDirectMessages",
        (current: WorkspaceDirectMessage[] = []) =>
          current.map((member) =>
            member.companyUserId === event.companyUserId
              ? {
                  ...member,
                  presenceStatus: event.status,
                  lastHeartbeatAt: event.lastHeartbeatAt,
                  presenceExpiresAt: event.presenceExpiresAt,
                }
              : member,
          ),
        { guard: workspaceMutationGuard({ companyId, userId: currentUserId }) },
      );
    },
    [companyId, currentUserId],
  );
  const handleWorkspaceRealtime = useCallback(
    (event: WorkspaceRealtimeEvent) => {
      if (!companyId || !currentUserId) {
        return;
      }
      const guard = workspaceMutationGuard({ companyId, userId: currentUserId });

      if (event.type === "workspace_conversation_activity") {
        const delta = event.senderUserId === currentUserId ? 0 : event.unreadDelta;

        if (event.target === "team") {
          router.replaceProp(
            "workspaceTeams",
            (current: WorkspaceTeam[] = []) =>
              current.map((team) =>
                team.conversationId === event.conversationId
                  ? {
                      ...team,
                      unreadCount: nextUnreadCount(team.unreadCount, delta, event.unreadCount),
                      lastMessageAt: event.latestMessageAt,
                    }
                  : team,
              ),
            { guard },
          );
          return;
        }

        if (event.target === "channel") {
          router.replaceProp(
            "workspaceChannels",
            (current: WorkspaceChannel[] = []) =>
              current.map((channel) =>
                channel.conversationId === event.conversationId
                  ? {
                      ...channel,
                      unreadCount: nextUnreadCount(channel.unreadCount, delta, event.unreadCount),
                      lastMessageAt: event.latestMessageAt,
                    }
                  : channel,
              ),
            { guard },
          );
          return;
        }

        router.replaceProp(
          "workspaceDirectMessages",
          (current: WorkspaceDirectMessage[] = []) =>
            current.map((member) =>
              member.conversationId === event.conversationId
                ? {
                    ...member,
                    unreadCount: nextUnreadCount(member.unreadCount, delta, event.unreadCount),
                    lastMessageAt: event.latestMessageAt,
                  }
                : member,
            ),
          { guard },
        );
        return;
      }

      if (event.type === "workspace_conversation_muted") {
        if (event.target === "channel") {
          router.replaceProp(
            "workspaceChannels",
            (current: WorkspaceChannel[] = []) =>
              current.map((item) =>
                item.conversationId === event.conversationId
                  ? { ...item, muted: event.muted }
                  : item,
              ),
            { guard },
          );
          return;
        }

        if (event.target === "direct") {
          router.replaceProp(
            "workspaceDirectMessages",
            (current: WorkspaceDirectMessage[] = []) =>
              current.map((item) =>
                item.conversationId === event.conversationId
                  ? { ...item, muted: event.muted }
                  : item,
              ),
            { guard },
          );
        }
      }
    },
    [companyId, currentUserId],
  );
  const handlePresenceUpdate = useCallback(
    (event: CompanyUserPresenceUpdateEvent) => {
      if (event.type !== "company_user_presence_updated") {
        return;
      }

      replaceDirectMessagePresence(event);
    },
    [replaceDirectMessagePresence],
  );

  useTransmitChannel<WorkspaceRealtimeEvent>(workspaceSidebarCompanyTopic, handleWorkspaceRealtime);
  useTransmitChannel<WorkspaceRealtimeEvent>(workspaceSidebarUserTopic, handleWorkspaceRealtime);
  useTransmitChannel<CompanyUserPresenceUpdateEvent>(presenceTopic, handlePresenceUpdate);

  useEffect(() => {
    if (!companyId || !currentUserId) {
      return;
    }

    const sendHeartbeat = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      void axios.post(urlFor("presence.heartbeat")).catch((error) => {
        if (!isCancel(error)) {
          console.error("Failed to send presence heartbeat", error);
        }
      });
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 30_000);
    document.addEventListener("visibilitychange", sendHeartbeat);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", sendHeartbeat);
    };
  }, [companyId, currentUserId]);

  useEffect(() => {
    if (!companyId || !currentUserId) {
      return;
    }
    const guard = workspaceMutationGuard({ companyId, userId: currentUserId });

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      // Cheap preflight against the last-known snapshot: skip the router call
      // entirely (not just rely on the patched core's post-hoc no-op check)
      // when no member has actually expired since the last tick.
      if (!hasExpiredPresenceMember(directMessagesRef.current, now)) {
        return;
      }

      router.replaceProp(
        "workspaceDirectMessages",
        (current: WorkspaceDirectMessage[] = []) =>
          current.map((member) => {
            if (!member.presenceExpiresAt || member.presenceStatus === "offline") {
              return member;
            }

            const expiresAt = Date.parse(member.presenceExpiresAt);
            if (!Number.isFinite(expiresAt) || expiresAt > now) {
              return member;
            }

            return {
              ...member,
              presenceStatus: "offline",
              lastHeartbeatAt: null,
              presenceExpiresAt: null,
            };
          }),
        { guard },
      );
    }, 10_000);

    return () => window.clearInterval(intervalId);
  }, [companyId, currentUserId]);

  const handleStatusChange = (value: string) => {
    router.put(
      urlFor("profile.status.update"),
      { status: value },
      {
        only: ["auth"],
        preserveScroll: true,
        preserveState: true,
      },
    );
  };

  return (
    <div className="bg-muted/35 flex h-dvh w-full max-w-full overflow-hidden text-foreground">
      <div className="flex h-dvh min-w-0 w-full max-w-full flex-1 flex-col overflow-hidden p-0 lg:p-2">
        <div className="bg-background flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-0 shadow-none lg:rounded-2xl lg:border lg:shadow-sm">
          <header
            className={cn(
              "bg-background/95 sticky top-0 z-40 border-b backdrop-blur",
              hideHeaderOnMobile && "hidden md:block",
            )}
          >
            <div className="flex h-14 min-w-0 items-center gap-3 overflow-hidden px-3 sm:px-4">
              <Link
                href={urlFor("dashboard")}
                prefetch
                title="Home"
                className="text-muted-foreground hover:bg-accent grid h-11 shrink-0 place-items-center rounded-md px-2"
              >
                {workspaceBrand?.logoUrl ? (
                  <img
                    src={workspaceBrand.logoUrl}
                    alt={auth.company?.name ?? "Logo"}
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <AppLogoIcon className="h-10 w-auto" />
                )}
              </Link>

              <WorkspaceTopNavigation
                destinations={barDestinations}
                pageUrl={page.url}
                activeTicketSection={activeTicketSection}
                showLabels={showTopNavigationLabels}
                navigationIndicators={navigationIndicators}
                trailing={
                  navigation ? (
                    <NavLauncher
                      launcher={navigation.launcher}
                      bar={navigation.bar}
                      navigationIndicators={navigationIndicators}
                      internalHasUnread={hasInternalUnread}
                    />
                  ) : undefined
                }
              />

              <div className="ml-auto flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("command_palette_open", "Buscar (⌘K)")}
                  title="⌘K"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))}
                >
                  <Search className="size-4" />
                </Button>
                <DisplayNotifications />
                <WorkspaceUserMenu
                  auth={auth}
                  status={status}
                  statusOptions={statusOptions}
                  currentStatusOption={currentStatusOption}
                  settingsDestination={settingsDestination}
                  onStatusChange={handleStatusChange}
                />
              </div>
            </div>
          </header>

          {navigation && editMode && (
            <div className="bg-muted/30 border-b px-3 py-2 sm:px-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground mr-1 text-xs">
                  {t("nav_editor.edit_hint", {
                    defaultValue: "Arrastra para reordenar, quita con ✕ o añade apps.",
                  })}
                </span>
                <NavBarEditor
                  bar={navigation.bar}
                  launcher={navigation.launcher}
                  onChange={handlePersonalNavChange}
                  className="min-w-0 flex-1"
                />
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        {t("nav_editor.reset_company", {
                          defaultValue: "Restablecer barra de la empresa",
                        })}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t("nav_editor.reset_confirm_title", {
                            defaultValue: "¿Volver a la barra predeterminada de la empresa?",
                          })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("nav_editor.reset_confirm_body", {
                            defaultValue: "Perderás tu orden y accesos personalizados.",
                          })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t("nav_editor.reset_confirm_cancel", { defaultValue: "Cancelar" })}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleResetPersonalNav}>
                          {t("nav_editor.reset_confirm_ok", { defaultValue: "Restablecer" })}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button size="sm" onClick={() => setEditMode(false)}>
                    {t("nav_editor.done", { defaultValue: "Listo" })}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <main className="min-h-0 min-w-0 max-w-full flex-1 overflow-y-auto overflow-x-hidden bg-background">
            <CommandPalette />
            <ShiftReminderBanner />
            <div className="h-full min-h-0 min-w-0 max-w-full overflow-x-hidden">
              {bottomBar ? <div className="min-w-0 pb-24">{children}</div> : children}
            </div>
          </main>
        </div>

        {bottomBar && (
          <div className="bg-background/95 fixed inset-x-0 bottom-0 z-50 border-t px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1),0_-2px_4px_-1px_rgba(0,0,0,0.06)] backdrop-blur">
            {bottomBar}
          </div>
        )}
      </div>

      <InternalShareDropTray members={directMessages} channels={workspaceChannels} teams={teams} />
    </div>
  );
}
